// src/components/ProductList/ProductListContent.js
import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  getCountryCurrency,
  getCountryMeasurementSystem,
  normalizeCountryName,
} from "../../services/countryService";
import { useMealPlan } from "../../contexts/MealPlanContext";
import backendClient from "../../services/backendClient";
import {
  trackGroceryListViewed,
  trackGroceryItemToggled,
  TOGGLE_STATE,
  OPEN_TRIGGER,
} from "../../analytics";
import ImagePreviewDrawer from "./admin/ImagePreviewDrawer";
import ReplaceProductDrawer from "./admin/ReplaceProductDrawer";
import SizeAdjustDrawer from "./admin/SizeAdjustDrawer";
import useCategoryScroll from "./hooks/useCategoryScroll";
import useProductPricing from "./hooks/useProductPricing";
import useStorageGroups from "./hooks/useStorageGroups";
import {
  getLocalizedProductName,
  getProductImage,
  translateCategory,
  getCategoryIcon,
  getSearchDocImage,
  getSearchDocPrice,
  getSearchDocAmountLabel,
} from "./utils/productListUtils";
import { calculateDisplayDetails as calculateDisplayDetailsUtil } from "./utils/displayDetailCalculators";
import { formatShelfLife } from "../../utils/freshnessDisplay";
import LeftoverItem from "./components/LeftoverItem";
import ProductItem from "./components/ProductItem";
import AddProductDrawer from "./AddProductDrawer";

// ----------------------------------------------------------------------------
// ProductListContent — shared inner content for drawer and full-page variants.
//
// Props:
//   products, maxStorageTime, loading, processingStatus, leftovers, country,
//   shoppingMode, isAdmin — same as original ProductList.
//   variant: 'drawer' | 'page' — controls presentational chrome.
//   onRequestClose: optional, rendered as close button (drawer variant only).
// ----------------------------------------------------------------------------
const ProductListContent = ({
  products,
  maxStorageTime,
  loading,
  processingStatus,
  leftovers,
  country = "Spain",
  shoppingMode = false,
  isAdmin = false,
  variant = "drawer",
  onRequestClose,
  checkedItemsOverride = null,
  onCheckedItemsChange,
  myProductsOverride = null,
}) => {
  const { t, i18n } = useTranslation("mealplan");
  const [activeMainTab, setActiveMainTab] = useState("groceries");
  const [groupedProducts, setGroupedProducts] = useState([]);
  const [checkedItems, setCheckedItems] = useState({});
  const [previewImage, setPreviewImage] = useState(null);
  const [replaceProduct, setReplaceProduct] = useState(null);
  const [sizeAdjustProduct, setSizeAdjustProduct] = useState(null);
  const [addProductOpen, setAddProductOpen] = useState(false);

  const isDrawer = variant === "drawer";

  const {
    activeCategory,
    categoryRefs,
    scrollRootRef,
    navTabsRef,
    scrollToCategory,
  } = useCategoryScroll({
    groupedProducts,
    isDrawer,
    activeMainTab,
    onMainTabChange: setActiveMainTab,
  });

  // Handle size update - update the grocery list item's sizes
  const handleSizeUpdated = ({ productId, newSizes }) => {
    setGroupedProducts((prevGroups) => {
      return prevGroups.map((category) => ({
        ...category,
        items: category.items.map((item) => {
          if (item.productId === productId) {
            const newSelectedSize = newSizes?.[0] || item.selectedSize;
            return {
              ...item,
              selectedSize: newSelectedSize,
              product: {
                ...item.product,
                sizes: newSizes,
              },
            };
          }
          return item;
        }),
      }));
    });
    console.log("✅ Updated grocery list with new sizes for product:", productId);
  };

  // Handle product replacement
  const handleProductReplaced = ({ oldProductId, newProduct, ingredientName }) => {
    setGroupedProducts((prevGroups) => {
      return prevGroups.map((category) => ({
        ...category,
        items: category.items.map((item) => {
          const itemIngredientName = item.originalName || item.name;
          if (
            itemIngredientName?.toLowerCase() === ingredientName?.toLowerCase() ||
            item.productId === oldProductId
          ) {
            const newSelectedSize = newProduct.sizes?.[0] || {
              name: newProduct.displayName || newProduct.name,
              image: newProduct.thumbnail || newProduct.image,
              type: "package",
              price: parseFloat(newProduct.price) || 0,
              weight: parseFloat(newProduct.weight) || null,
              weightUnit: newProduct.weightUnit || "g",
              description: newProduct.displayName || newProduct.name,
            };

            return {
              ...item,
              productId: newProduct.id,
              productName: newProduct.displayName || newProduct.name,
              lang: newProduct.lang || null,
              category: newProduct.category || item.category,
              unmatched: false,
              matchConfidence: 1.0,
              aiReasoning: "Manually replaced by user",
              selectedSize: newSelectedSize,
              product: newProduct,
            };
          }
          return item;
        }),
      }));
    });
    console.log(
      "✅ Updated grocery list with replacement product:",
      newProduct.displayName || newProduct.name
    );
  };

  const translatePackageDescription = useCallback(
    (description) => {
      if (!description) return t("grocery.package");
      const normalized = description.toLowerCase().trim();
      const packMatch = normalized.match(/^pack[- ]?(\d+)$/i);
      if (packMatch) {
        const count = packMatch[1];
        return t("grocery.packageTypes.pack") + `-${count}`;
      }
      const translationKey = `grocery.packageTypes.${normalized}`;
      const translated = t(translationKey, { defaultValue: null });
      if (translated && translated !== translationKey) {
        return translated;
      }
      return description;
    },
    [t]
  );

  const {
    haveItState,
    updateHaveItState,
    shoppingModeState,
    setShoppingModeState,
    myProducts: ctxMyProducts,
    setMyProducts,
  } = useMealPlan();

  // When myProductsOverride is provided (shared/read-only view), use it and hide
  // the Add/remove controls. Otherwise use the editable context list.
  const isMyProductsReadOnly = Array.isArray(myProductsOverride);
  const myProducts = isMyProductsReadOnly ? myProductsOverride : (ctxMyProducts || []);

  const toggleMyProduct = useCallback(
    (doc) => {
      if (!doc || !doc.id) return;
      setMyProducts((prev) => {
        const list = prev || [];
        const exists = list.some((p) => p.id === doc.id);
        if (exists) {
          return list.filter((p) => p.id !== doc.id);
        }
        return [...list, doc];
      });
    },
    [setMyProducts]
  );

  const removeMyProduct = useCallback(
    (id) => {
      setMyProducts((prev) => (prev || []).filter((p) => p.id !== id));
    },
    [setMyProducts]
  );

  // Persist user-added "My products" to the saved meal plan so they survive a
  // refresh and are included in shared grocery links. Runs in any editable
  // (non read-only) variant — both the MealPlan drawer and the groceries page.
  // Uses a dedicated endpoint that updates only myProducts, so it works even
  // when the full meal plan isn't present in context (e.g. the groceries page).
  const myProductsSaveInitedRef = useRef(false);
  useEffect(() => {
    if (isMyProductsReadOnly) return;
    if (!myProductsSaveInitedRef.current) {
      myProductsSaveInitedRef.current = true;
      return;
    }
    const token = localStorage.getItem("authToken");
    if (!token) return;

    const timeoutId = setTimeout(() => {
      backendClient
        .patch("/api/mealplan/my-products", { myProducts: ctxMyProducts || [] }, { timeout: 30000 })
        .catch((error) => {
          console.error("Error saving myProducts:", error);
        });
    }, 600);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctxMyProducts]);

  const effectiveHaveItState = useMemo(() => haveItState || {}, [haveItState]);

  useEffect(() => {
    if (!shoppingMode) {
      return;
    }

    if (checkedItemsOverride) {
      setCheckedItems(checkedItemsOverride);
      return;
    }

    if (shoppingMode) {
      const mergedState = {
        ...effectiveHaveItState,
        ...(shoppingModeState || {}),
      };
      console.log(
        "🔄 ProductListContent - Syncing checkedItems with merged state:",
        mergedState
      );
      setCheckedItems(mergedState);
    }
  }, [shoppingMode, shoppingModeState, effectiveHaveItState, checkedItemsOverride]);

  const normalizedCountry = normalizeCountryName(country);
  const currencyInfo = getCountryCurrency(normalizedCountry);
  const measurementSystem = getCountryMeasurementSystem(normalizedCountry);

  // Enhanced fallback logic
  const getFallbackValue = (value, fallback) => {
    if (
      value == null ||
      value === "undefined" ||
      value === "" ||
      Number.isNaN(value)
    ) {
      return fallback;
    }
    if (typeof value === "string" && value.trim() === "") {
      return fallback;
    }
    return value;
  };

  // Normalize product structure (same as original ProductList)
  const processProducts = (products) => {
    return products.map((category) => ({
      ...category,
      items: category.items.map((item) => {
        const selectedSize = item.selectedSize || {};
        const productType = selectedSize.type || item.type || "package";

        const baseItem = {
          ...item,
          productName: getFallbackValue(item.productName, "Unknown Product"),
          totalQty: getFallbackValue(item.totalQty, 0),
          unit: getFallbackValue(item.unit, "unit"),
          totalPrice: getFallbackValue(item.totalPrice, 0),
          ai_data: item.ai_data || false,
          type: productType,
          selectedSize: selectedSize,
        };

        if (productType === "package") {
          return {
            ...baseItem,
            type: "package",
            amount: getFallbackValue(
              selectedSize.weight || selectedSize.itemsQty || item.amount,
              0
            ),
            measurementUnit: getFallbackValue(
              selectedSize.weightUnit || item.measurementUnit,
              "unit"
            ),
            pricePerPackage: getFallbackValue(
              selectedSize.price || item.pricePerPackage,
              0
            ),
            description: getFallbackValue(
              selectedSize.description || item.description,
              "Pack"
            ),
            leftoverQty: getFallbackValue(item.leftoverQty, null),
          };
        } else if (productType === "weight") {
          return {
            ...baseItem,
            type: "weight",
            pricePerWeight: getFallbackValue(
              selectedSize.price || item.pricePerWeight,
              0
            ),
            measurementUnit: getFallbackValue(
              selectedSize.weightUnit || item.measurementUnit,
              measurementSystem === "metric" ? "g" : "oz"
            ),
            marketWeight: getFallbackValue(item.marketWeight, {}),
            avgWeight: getFallbackValue(
              selectedSize.avgWeight || item.avgWeight,
              {}
            ),
          };
        } else if (productType === "unit") {
          return {
            ...baseItem,
            type: "unit",
            pricePerUnit: getFallbackValue(
              selectedSize.price || item.pricePerUnit,
              0
            ),
            measurementUnit: getFallbackValue(
              selectedSize.weightUnit || item.measurementUnit,
              "piece"
            ),
            avgWeight: getFallbackValue(
              selectedSize.avgWeight || item.avgWeight,
              {}
            ),
          };
        }

        return baseItem;
      }),
    }));
  };

  useEffect(() => {
    if (!Array.isArray(products)) {
      setGroupedProducts([]);
      return;
    }
    const visibleProducts = isAdmin
      ? products
      : products
          .map((cat) => ({
            ...cat,
            items: (cat.items || []).filter((item) => item.source !== "ai"),
          }))
          .filter((cat) => (cat.items || []).length > 0);
    const processedProducts = processProducts(visibleProducts);
    setGroupedProducts(processedProducts);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, isAdmin]);

  // Track Grocery List Viewed when products are loaded
  const hasTrackedViewed = useRef(false);
  useEffect(() => {
    if (groupedProducts.length > 0 && !hasTrackedViewed.current) {
      hasTrackedViewed.current = true;

      const productsCount = groupedProducts.reduce(
        (sum, cat) => sum + cat.items.length,
        0
      );
      const haveItCount = Object.values(haveItState || {}).filter(Boolean).length;
      const leftoversCount = leftovers?.length || 0;

      const calculatedTotalCost = groupedProducts.reduce((sum, cat) => {
        return (
          sum +
          cat.items.reduce((c, item) => {
            if (item.unmatched === true || item.error) return c;
            const num = parseFloat(
              (item.totalPrice || "").toString().replace(/[^0-9.-]/g, "")
            );
            return c + (isNaN(num) ? 0 : num);
          }, 0)
        );
      }, 0);

      trackGroceryListViewed({
        products_count: productsCount,
        total_cost: Math.round(calculatedTotalCost * 100) / 100,
        have_it_count: haveItCount,
        open_trigger: OPEN_TRIGGER.BUTTON,
        leftovers_count: leftoversCount,
      });
    }
  }, [groupedProducts, haveItState, leftovers]);

  const toggleHaveIt = useCallback(
    (name, categoryName = null, productId = null, price = null) => {
      if (!updateHaveItState) return;

      const currentHasIt = effectiveHaveItState[name] || false;
      const newHasIt = !currentHasIt;

      trackGroceryItemToggled({
        toggle_state: newHasIt ? TOGGLE_STATE.ON : TOGGLE_STATE.OFF,
        product_category: categoryName,
        product_id: productId,
        price: price,
        toggle_source: "product_list",
      });

      updateHaveItState(name, newHasIt, groupedProducts, () => currencyInfo);
    },
    [effectiveHaveItState, groupedProducts, currencyInfo, updateHaveItState]
  );

  const toggleChecked = useCallback(
    (productName) => {
      setCheckedItems((prev) => {
        const next = { ...prev, [productName]: !prev[productName] };
        if (onCheckedItemsChange) {
          onCheckedItemsChange(next);
        } else if (setShoppingModeState) {
          setShoppingModeState(next);
        }
        return next;
      });
    },
    [onCheckedItemsChange, setShoppingModeState]
  );

  const { totalPrice, adjustedTotalPrice, formatMoney } = useProductPricing(
    groupedProducts,
    effectiveHaveItState,
    currencyInfo
  );

  // Map country → representative supermarket chain used for the price data.
  // Falls back to the translated country name when no chain is mapped.
  const supermarketByCountry = {
    Spain: "Mercadona",
    Estonia: "Selver",
    USA: "Walmart",
  };

  const getShoppingEntityName = () => {
    const supermarket = supermarketByCountry[normalizedCountry];
    if (supermarket) return supermarket;
    return t(`grocery.countryNames.${normalizedCountry}`, {
      defaultValue: normalizedCountry,
    });
  };

  const calculateDisplayDetails = useCallback(
    (item) =>
      calculateDisplayDetailsUtil({
        item,
        t,
        measurementSystem,
        currencySymbol: currencyInfo.symbol,
        formatMoney,
        translatePackageDescription,
      }),
    [t, measurementSystem, currencyInfo.symbol, formatMoney, translatePackageDescription]
  );

  const uniqueCategories = groupedProducts.map((g) => g.category);

  const storageGroups = useStorageGroups({
    groupedProducts,
    t,
    getFallbackValue,
    getProductImage,
  });


  // --------------------------------------------------------------------------
  // Render
  // --------------------------------------------------------------------------

  // Sticky bottom nav: floating pill style matching MealPlan StickyBottomBar.
  // Both drawer and page variants use the same pill positioning.
  // The nav renders only on the groceries tab.
  const stickyNavClass = "fixed bottom-[16px] left-[20px] right-[20px] mx-auto max-w-5xl";

  return (
    <>
      {/* Header: tabs + optional close button */}
      <div className="flex-shrink-0 pt-4 pb-0 relative">
        {isDrawer && onRequestClose && (
          <button
            onClick={onRequestClose}
            className="absolute top-1 right-1 flex justify-center items-center md:hover:bg-gray-200 w-[36px] h-[36px] rounded-[12px] transition-colors z-10"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path
                d="M16.381 6.38079C16.7227 6.03908 17.2776 6.03908 17.6193 6.38079C17.961 6.7225 17.961 7.27737 17.6193 7.61907L13.2385 11.9999L17.6193 16.3808L17.6789 16.4472C17.9595 16.7909 17.9398 17.2986 17.6193 17.6191C17.2989 17.9395 16.7911 17.9592 16.4474 17.6786L16.381 17.6191L12.0002 13.2382L7.61932 17.6191C7.27761 17.9608 6.72275 17.9608 6.38104 17.6191C6.03933 17.2774 6.03933 16.7225 6.38104 16.3808L10.7619 11.9999L6.38104 7.61907L6.32147 7.55267C6.04089 7.20899 6.06059 6.70124 6.38104 6.38079C6.70149 6.06034 7.20923 6.04064 7.55291 6.32122L7.61932 6.38079L12.0002 10.7617L16.381 6.38079Z"
                fill="#56554D"
              />
            </svg>
          </button>
        )}

        <div className="px-0 flex border-b border-[var(--Color-Grays-Gray-3,#E8E6E3)]">
          <div
            onClick={() => setActiveMainTab("groceries")}
            className={`flex-1 py-3 text-center justify-center flex items-center border-b-[2px] cursor-pointer ${
              activeMainTab === "groceries"
                ? "border-[#FFCB00] text-[var(--Color-Grays-Gray-10,#0D0C07)]  font-semibold"
                : "text-[var(--Color-Grays-Gray-7,#65635D)] font-normal border-[transparent]"
            }`}
          >
            <span className="flex items-center justify-center w-[16px] h-[16px]">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M7 21H17M19.5 12L22 6M16.25 3C16.52 3.1 17.05 3.53 17 4.36C16.94 5.19 16.07 5.56 16 6.38C15.95 7.16 16.34 7.62 16.73 8M11.25 3C11.52 3.1 12.05 3.53 11.99 4.36C11.94 5.19 11.06 5.56 11.01 6.38C10.95 7.16 11.34 7.62 11.73 8M6.25 3C6.52 3.1 7.05 3.53 7 4.36C6.94 5.19 6.07 5.56 6 6.38C5.95 7.16 6.34 7.62 6.74 8M12 21C14.3869 21 16.6761 20.0518 18.364 18.364C20.0518 16.6761 21 14.3869 21 12H3C3 14.3869 3.94821 16.6761 5.63604 18.364C7.32387 20.0518 9.61305 21 12 21Z"
                  stroke={activeMainTab === "groceries" ? "#0D0C07" : "#65635D"}
                  strokeWidth="1.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span className="ml-1 text-[14.8px] leading-[28px] h-7 py-0">
              {t("grocery.groceriesTab")}
            </span>
          </div>
          <div
            onClick={() => setActiveMainTab("storage")}
            className={`flex-1 py-3 text-center justify-center flex items-center border-b-[2px] cursor-pointer ${
              activeMainTab === "storage"
                ? "border-[#FFCB00] text-[var(--Color-Grays-Gray-10,#0D0C07)] font-semibold"
                : "text-[var(--Color-Grays-Gray-7,#65635D)] font-normal border-[transparent]"
            }`}
          >
            <span className="flex items-center justify-center w-[16px] h-[16px]">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M5 10H19M15 7V13M5 6C5 4.93913 5.42143 3.92172 6.17157 3.17157C6.92172 2.42143 7.93913 2 9 2H15C16.0609 2 17.0783 2.42143 17.8284 3.17157C18.5786 3.92172 19 4.93913 19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6Z"
                  stroke={activeMainTab === "storage" ? "#0D0C07" : "#65635D"}
                  strokeWidth="1.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span className="ml-1 text-[14.8px] leading-[28px] h-7 py-0">
              {t("grocery.storageTab")}
            </span>
          </div>
          <div
            onClick={() => setActiveMainTab("leftovers")}
            className={`flex-1 py-3 text-center justify-center flex items-center border-b-[2px] cursor-pointer ${
              activeMainTab === "leftovers"
                ? "border-[#FFCB00] text-[var(--Color-Grays-Gray-10,#0D0C07)] font-semibold"
                : "text-[var(--Color-Grays-Gray-7,#65635D)] font-normal border-[transparent]"
            }`}
          >
            <span className="flex items-center justify-center w-[16px] h-[16px]">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path
                  d="M2 12H22M20 12V20C20 20.5305 19.7893 21.0392 19.4142 21.4143C19.0391 21.7893 18.5304 22 18 22H6C5.46957 22 4.96086 21.7893 4.58579 21.4143C4.21071 21.0392 4 20.5305 4 20V12M4 8.00004L20 4.00004M8.86 6.78004L8.41 4.97004C8.34553 4.71527 8.33189 4.4503 8.36988 4.19026C8.40787 3.93022 8.49674 3.68022 8.6314 3.45454C8.76606 3.22887 8.94388 3.03194 9.15469 2.87503C9.3655 2.71811 9.60517 2.60428 9.86 2.54004L11.8 2.06004C12.0554 1.99573 12.3211 1.98251 12.5816 2.02114C12.8422 2.05977 13.0926 2.14949 13.3184 2.28515C13.5441 2.42081 13.7409 2.59974 13.8974 2.81166C14.0538 3.02358 14.1669 3.26431 14.23 3.52004L14.68 5.32004"
                  stroke={activeMainTab === "leftovers" ? "#0D0C07" : "#65635D"}
                  strokeWidth="1.75"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
            <span className="ml-1 text-[14.8px] leading-[28px] h-7 py-0">
              {t("grocery.leftoversCount")}
            </span>
          </div>
        </div>
      </div>

      {/* Scrollable content: drawer variant takes remaining flex space with its own scroll.
          Page variant flows with document scroll — no internal overflow. */}
      <div
        ref={scrollRootRef}
        className={
          isDrawer
            ? "flex-1 overflow-y-auto overflow-x-hidden min-h-0 mt-5 bg-[#fafafa]"
            : "pt-5 bg-[#fafafa]"
        }
        style={
          isDrawer
            ? {
                overscrollBehavior: "contain",
                WebkitOverflowScrolling: "touch",
                willChange: "scroll-position",
              }
            : undefined
        }
      >
        <div className={isDrawer ? "px-5 pb-32" : "px-5 pb-5"}>
          <div className="space-y-6 pb-4">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="w-16 h-16 border-4 border-[var(--Color-Grays-Gray-3,#E8E6E3)] border-t-[var(--Color-Grays-Gray-7,#65635D)] rounded-full animate-spin mb-4" />
                <p className="text-[var(--Color-Grays-Gray-10,#0D0C07)] text-[16px] font-medium">
                  {processingStatus?.message ||
                    t("grocery.processingGroceryList")}
                </p>
              </div>
            ) : activeMainTab === "leftovers" ? (
              <div
                className="
                  rounded-[var(--Border-Radius-Outer,12px)]
                  border-[var(--Borders-Base-Width,0.5px)]
                  border-[var(--Color-Grays-Gray-2,#D7D7D5)]
                  overflow-hidden
                  border-[var(--Color-Grays-Gray-3,#D7D7D5)]
                  border-[0.5px]
                "
              >
                <h3 className="text-[var(--Color-Grays-Gray-10,#0D0C07)] text-[16px] bg-[#F3F3F2] font-semibold leading-[28px] px-4 py-3 flex items-center">
                  <span className="mr-3 text-[18px]">📦</span>
                  {t("grocery.leftoverItems")}
                </h3>
                <div className="space-y-0">
                  {leftovers
                    ?.filter((it) => isAdmin || it.source !== "ai")
                    .map((it, idx) => (
                      <LeftoverItem
                        key={idx}
                        item={it}
                        onPreview={setPreviewImage}
                      />
                    ))}
                </div>
              </div>
            ) : activeMainTab === "storage" ? (
              <div className="space-y-5">
                {/* <div className="bg-[#ffcb00] relative rounded-[12px]">
                  <div className="flex gap-3 items-start px-4 py-3">
                    <div className="flex items-center justify-center h-7 py-1 shrink-0">
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path
                          d="M8.3335 1.66667H11.6668M10.0002 11.6667V8.33334M3.3335 10.8333C3.53755 9.21332 4.32904 7.72451 5.55787 6.64929C6.78669 5.57407 8.36739 4.9872 10.0002 5.00001C11.0785 5.00115 12.1405 5.26387 13.0951 5.76564C14.0496 6.2674 14.8681 6.99323 15.4805 7.88086C16.0929 8.76848 16.4808 9.79142 16.611 10.8619C16.7412 11.9324 16.6097 13.0185 16.2279 14.027C15.8461 15.0355 15.2254 15.9364 14.4189 16.6523C13.6125 17.3682 12.6444 17.8778 11.5977 18.1373C10.551 18.3969 9.45703 18.3987 8.40952 18.1425C7.36201 17.8864 6.39228 17.3799 5.5835 16.6667L3.3335 14.6667M7.50016 14.1667H3.3335V18.3333"
                          stroke="#0D0C07"
                          strokeWidth="1.75"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                    <p className="text-[#0d0c07] text-[15.2px] leading-[28px]">
                      {t("grocery.storage.tip")}
                    </p>
                  </div>
                </div>*/}

                {Object.entries(storageGroups).map(([key, group]) => {
                  if (group.items.length === 0) return null;
                  return (
                    <div
                      key={key}
                      className="
                        rounded-[var(--Border-Radius-Outer,12px)]
                        border-[0.5px]
                        border-[var(--Color-Grays-Gray-3,#D7D7D5)]
                        overflow-hidden
                      "
                    >
                      <h3 className="text-[var(--Color-Grays-Gray-10,#0D0C07)] text-[16px] bg-[#F3F3F2] font-semibold leading-[28px] px-4 py-3 flex items-center justify-between">
                        <div className="flex items-center">
                          <span className="mr-3 text-[18px]">{group.icon}</span>
                          {group.label}
                        </div>
                        <span className="text-[12px] font-normal text-[var(--Color-Grays-Gray-7,#65635D)] bg-[var(--Color-Grays-Gray-2,#F5F3F0)] px-2 py-0.5 rounded-full">
                          {group.items.length}
                        </span>
                      </h3>
                      <div className="space-y-0">
                        {group.items.map((item, idx) => (
                          <div
                            key={idx}
                            className="bg-[#fff] px-4 py-3 border-b border-[var(--Color-Grays-Gray-3,#E8E6E3)] border-[0.5px] flex items-center gap-3"
                          >
                            <div className="flex-shrink-0 w-10 h-10 rounded-[8px] overflow-hidden bg-gray-100">
                              <img
                                src={item.image}
                                alt={item.productName}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.src = "/assets/products/no-image.jpg";
                                }}
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-[var(--Color-Grays-Gray-10,#0D0C07)] text-[14px] font-medium leading-[20px] truncate">
                                {getLocalizedProductName(item, i18n.language)}
                              </p>
                              <p className="text-[var(--Color-Grays-Gray-7,#65635D)] text-[12px] leading-[16px]">
                                {translateCategory(item.category, t)}
                              </p>
                            </div>
                            {item.shelfLifeDays > 0 && (
                              <div
                                className={`flex-shrink-0 text-[12px] font-medium px-2.5 py-1 rounded-full ${
                                  item.shelfLifeDays <= 3
                                    ? "bg-[#FEF3C7] text-[#92400E]"
                                    : item.shelfLifeDays <= 7
                                    ? "bg-[#DCFCE7] text-[#166534]"
                                    : "bg-[var(--Color-Grays-Gray-2,#F5F3F0)] text-[var(--Color-Grays-Gray-7,#65635D)]"
                                }`}
                              >
                                {formatShelfLife(t, item.shelfLifeDays)}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <>
                {/* Add-your-own-products prompt — hidden in the reviewmeals
                    drawer variant (entry point only lives on the page variant) */}
                {!isMyProductsReadOnly && !isDrawer && (
                <div className="flex items-center gap-3 bg-white border-[0.5px] border-[var(--Color-Grays-Gray-3,#E8E6E3)] rounded-[12px] px-4 py-3">
                  <p className="flex-1 text-[14px] leading-[20px] text-[#0D0C07]">
                    {t("grocery.myProducts.prompt")}
                  </p>
                  <button
                    type="button"
                    onClick={() => setAddProductOpen(true)}
                    className="flex-shrink-0 inline-flex items-center gap-1 h-9 px-4 rounded-[12px] bg-[#060] text-white text-[14px] font-semibold leading-none transition-colors md:hover:bg-[#090]"
                  >
                    {t("grocery.myProducts.addButton")}
                  </button>
                </div>
                )}

                {/* My products block */}
                {myProducts.length > 0 && (
                  <div className="rounded-[12px] border-[0.5px] border-[var(--Color-Grays-Gray-3,#D7D7D5)] overflow-hidden">
                    <h3 className="text-[var(--Color-Grays-Gray-10,#0D0C07)] text-[16px] bg-[#F3F3F2] font-semibold leading-[28px] px-4 py-3 flex items-center border-b-[0.5px] border-[var(--Color-Grays-Gray-3,#D7D7D5)]">
                      <span className="mr-3 text-[18px]">🛒</span>
                      {t("grocery.myProducts.title")}
                    </h3>
                    <div className="space-y-0">
                      {myProducts.map((doc) => {
                        const name = getLocalizedProductName(doc, i18n.language);
                        const image = getSearchDocImage(doc);
                        const amountLabel = getSearchDocAmountLabel(doc, t);
                        const price = getSearchDocPrice(doc);
                        return (
                          <div
                            key={doc.id}
                            className="bg-[#fff] px-4 py-3 border-b border-[var(--Color-Grays-Gray-3,#E8E6E3)] border-[0.5px] flex items-center gap-3"
                          >
                            <div className="flex-shrink-0 w-10 h-10 rounded-[8px] overflow-hidden bg-gray-100">
                              <img
                                src={image}
                                alt={name}
                                className="w-full h-full object-cover"
                                onError={(e) => {
                                  e.target.src = "/assets/products/no-image.jpg";
                                }}
                              />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-[var(--Color-Grays-Gray-10,#0D0C07)] text-[14px] font-medium leading-[20px] truncate">
                                {name}
                              </p>
                              {amountLabel && (
                                <p className="text-[var(--Color-Grays-Gray-7,#65635D)] text-[12px] leading-[16px]">
                                  {amountLabel}
                                </p>
                              )}
                            </div>
                            {price != null && (
                              <span className="flex-shrink-0 text-[14px] font-semibold text-[#0D0C07] whitespace-nowrap">
                                {currencyInfo.symbol}
                                {price.toFixed(2)}
                              </span>
                            )}
                            {!isMyProductsReadOnly && (
                            <button
                              type="button"
                              onClick={() => removeMyProduct(doc.id)}
                              aria-label={t("grocery.myProducts.remove")}
                              className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center bg-[#F5F3F0] text-[#65635D] transition-colors md:hover:bg-[#E8E6E3]"
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none">
                                <path
                                  d="M18 6L6 18M6 6L18 18"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {isDrawer && (
                <div className="bg-[#FFCB00] rounded-[12px] px-4 py-3">
                  <span className="text-[15.2px] leading-[28px] text-[#0D0C07]">
                    {t("grocery.pricesFrom", { supermarket: getShoppingEntityName() })}
                  </span>
                </div>
                )}
                {groupedProducts.map((cat, cIdx) => (
                  <div
                    key={cIdx}
                    ref={(el) => (categoryRefs.current[cat.category] = el)}
                    className="rounded-b-[12px] border-x-[0.5px] border-b-[0.5px] border-[var(--Color-Grays-Gray-3,#D7D7D5)]"
                  >
                    <h3 className="sticky top-0 z-10 text-[var(--Color-Grays-Gray-10,#0D0C07)] text-[16px] bg-[#F3F3F2] font-semibold leading-[28px] px-4 py-3 flex items-center border-y-[0.5px] border-[var(--Color-Grays-Gray-3,#D7D7D5)]">
                      <span className="mr-3 text-[18px]">
                        {getCategoryIcon(cat.category)}
                      </span>
                      {translateCategory(cat.category, t)}
                    </h3>
                  <div className="space-y-0 rounded-b-[12px] overflow-hidden">
                    {cat.items.map((item, iIdx) => {
                      const productKey =
                        item.productName ||
                        getLocalizedProductName(item, i18n.language);
                      return (
                        <ProductItem
                          key={iIdx}
                          item={item}
                          categoryName={cat.category}
                          isAdmin={isAdmin}
                          shoppingMode={shoppingMode}
                          hasIt={!!effectiveHaveItState[productKey]}
                          isChecked={!!checkedItems[productKey]}
                          calculateDisplayDetails={calculateDisplayDetails}
                          onPreviewImage={setPreviewImage}
                          onToggleHaveIt={toggleHaveIt}
                          onToggleChecked={toggleChecked}
                          onRequestReplace={setReplaceProduct}
                          onRequestSizeAdjust={setSizeAdjustProduct}
                        />
                      );
                    })}
                  </div>
                </div>
                ))}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Sticky Bottom Nav & Total - only on groceries tab */}
      {activeMainTab === "groceries" && !addProductOpen && (
        <div
          className={`${stickyNavClass}
            bg-[linear-gradient(135deg,_rgba(243,243,242,0.8)_0%,_rgba(243,243,242,0.9)_100%)]
            backdrop-blur-[64px]
            border-[1px] border-[#fff]
            rounded-[96px]
            shadow-[0px_0px_4px_0px_rgba(13,12,7,0.1),_1px_-2px_16px_0px_rgba(13,12,7,0.1)]
            py-2 px-2
            z-[99999]
            flex items-center
          `}
        >
          {/* Price — fixed left, non-scrollable (drawer only) */}
          {isDrawer && (
          <div className="flex-shrink-0 flex items-center pl-2 pr-3 mr-2 border-r border-[#D7D7D5] self-stretch">
            <span className="text-[14px] font-semibold text-[#0D0C07] whitespace-nowrap leading-none">
              {adjustedTotalPrice < totalPrice
                ? formatMoney(adjustedTotalPrice)
                : formatMoney(totalPrice)}
            </span>
          </div>
          )}
          {/* Categories — scrollable right */}
          <div ref={navTabsRef} className="scrollbar-hide overflow-x-auto overflow-y-visible flex-1 min-w-0">
            <div className="flex flex-nowrap gap-1 px-0 w-max mx-auto">
              {uniqueCategories.map((cat, i) => {
                const isActive = cat === activeCategory;
                return (
                  <button
                    key={i}
                    data-category-tab={cat}
                    onClick={() => scrollToCategory(cat)}
                    className={`px-3 py-2 rounded-[20px] text-[12.4px] whitespace-nowrap transition-colors ${
                      isActive
                        ? "bg-[#0D0C07] text-[#fff] font-normal"
                        : "bg-[var(--Color-Grays-Gray-2,#F5F3F0)] text-[var(--Color-Grays-Gray-10,#0D0C07)] font-normal md:hover:bg-[var(--Color-Grays-Gray-3,#E8E6E3)]"
                    }`}
                  >
                    {translateCategory(cat, t)}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Nested admin/image drawers */}
      <ImagePreviewDrawer
        isOpen={!!previewImage}
        image={previewImage}
        closeDrawer={() => setPreviewImage(null)}
      />
      <ReplaceProductDrawer
        isOpen={!!replaceProduct}
        ingredientName={replaceProduct?.ingredientName || ""}
        currentProductId={replaceProduct?.currentProductId || null}
        onProductReplaced={handleProductReplaced}
        closeDrawer={() => setReplaceProduct(null)}
      />
      <SizeAdjustDrawer
        isOpen={!!sizeAdjustProduct}
        productId={sizeAdjustProduct?.productId || ""}
        productName={sizeAdjustProduct?.productName || ""}
        currentSizes={sizeAdjustProduct?.currentSizes || []}
        onSizeUpdated={handleSizeUpdated}
        closeDrawer={() => setSizeAdjustProduct(null)}
      />
      <AddProductDrawer
        isOpen={addProductOpen}
        onClose={() => setAddProductOpen(false)}
        onToggleProduct={toggleMyProduct}
        selectedIds={myProducts.map((p) => p.id)}
        currencySymbol={currencyInfo.symbol}
      />
    </>
  );
};

export default ProductListContent;
